import type {
  LedgerEntryRow,
  RefundQueueRow,
  TopupQueueRow,
  WalletAccountListRow,
  WalletLimitsUpdatePayload,
  WalletLimitsView,
} from "@/modules/wallet/domain/types";
import { config } from "@/shared/lib/config";
import { fetcher } from "@/shared/lib/fetcher";

export type WalletAccountsListParams = {
  status?: string;
  search?: string;
  sortField?: "balanceLak" | "createdAt" | "customerName";
  sortOrder?: "asc" | "desc";
  limit?: number;
  offset?: number;
};

export type WalletAccountsListResult = {
  items: WalletAccountListRow[];
  total: number;
};

export type WalletSummary = {
  totalActiveWallet: number;
  totalBalance: number;
  suspendedCount: number;
  newToday: number;
};

export type TopupQueueListParams = {
  requestedFrom?: string;
  requestedTo?: string;
  search?: string;
  status?: "PENDING" | "APPROVED" | "REJECTED";
  method?: "CASHIER" | "BANK_SLIP";
  sortField?: "requestedAt";
  sortOrder?: "asc" | "desc";
  limit?: number;
  offset?: number;
};

export type TopupQueueListResult = {
  items: TopupQueueRow[];
  total: number;
};

export type TopupQueueSummary = {
  queueHealth: number;
  todaysApprovals: number;
  totalProcessed: number;
};

export type RefundQueueListParams = {
  createdFrom?: string;
  createdTo?: string;
  search?: string;
  sortField?: "createdAt";
  sortOrder?: "asc" | "desc";
  limit?: number;
  offset?: number;
};

export type RefundQueueListResult = {
  items: RefundQueueRow[];
  total: number;
};

export type RefundQueueSummary = {
  totalPendingRequests: number;
  totalPendingAmount: number;
  avgResponseTimeMinutes: number;
  autoApproveToday: number;
};

export type LedgerListParams = {
  walletId?: string;
  search?: string;
  createdFrom?: string;
  createdTo?: string;
  type?: string; // comma-separated TOPUP,PAYMENT,REFUND,ADJUSTMENT
  sortField?: "createdAt";
  sortOrder?: "asc" | "desc";
  limit?: number;
  offset?: number;
};

export type LedgerListResult = {
  items: LedgerEntryRow[];
  total: number;
};

export type LedgerSummary = {
  totalCredit: number;
  totalDebit: number;
  auditCoverage: number;
  netBalance: number;
};

export const walletApi = {
  async summary(): Promise<WalletSummary> {
    return fetcher.get<WalletSummary>(`${config.apiUrl}/wallet/summary`);
  },

  async getTopupQueueSummary(): Promise<TopupQueueSummary> {
    return fetcher.get<TopupQueueSummary>(
      `${config.apiUrl}/wallet/topup-queue/summary`,
    );
  },

  async listTopupQueue(
    params: TopupQueueListParams = {},
  ): Promise<TopupQueueListResult> {
    const searchParams = new URLSearchParams();
    if (params.requestedFrom) searchParams.set("requestedFrom", params.requestedFrom);
    if (params.requestedTo) searchParams.set("requestedTo", params.requestedTo);
    if (params.search) searchParams.set("search", params.search);
    if (params.status) searchParams.set("status", params.status);
    if (params.method) searchParams.set("method", params.method);
    if (params.sortField) searchParams.set("sortField", params.sortField);
    if (params.sortOrder) searchParams.set("sortOrder", params.sortOrder);
    if (params.limit != null) searchParams.set("limit", String(params.limit));
    if (params.offset != null) searchParams.set("offset", String(params.offset));
    return fetcher.get<TopupQueueListResult>(
      `${config.apiUrl}/wallet/topup-queue?${searchParams.toString()}`,
    );
  },

  async approveTopup(
    id: string,
    totpCode?: string,
    idempotencyKey?: string,
  ): Promise<{ ok: true } | { error: string }> {
    const res = await fetcher.post<{ ok: true } | { error: string }>(
      `${config.apiUrl}/wallet/topup-queue/${encodeURIComponent(id)}/approve`,
      totpCode ? { totpCode } : {},
      idempotencyKey ? { "Idempotency-Key": idempotencyKey } : undefined,
    );
    return res as { ok: true } | { error: string };
  },

  async rejectTopup(
    id: string,
    idempotencyKey?: string,
  ): Promise<{ ok: true } | { error: string }> {
    const res = await fetcher.post<{ ok: true } | { error: string }>(
      `${config.apiUrl}/wallet/topup-queue/${encodeURIComponent(id)}/reject`,
      {},
      idempotencyKey ? { "Idempotency-Key": idempotencyKey } : undefined,
    );
    return res as { ok: true } | { error: string };
  },

  async getRefundQueueSummary(): Promise<RefundQueueSummary> {
    return fetcher.get<RefundQueueSummary>(
      `${config.apiUrl}/wallet/refund-queue/summary`,
    );
  },

  async listRefundQueue(
    params: RefundQueueListParams = {},
  ): Promise<RefundQueueListResult> {
    const searchParams = new URLSearchParams();
    if (params.createdFrom) searchParams.set("createdFrom", params.createdFrom);
    if (params.createdTo) searchParams.set("createdTo", params.createdTo);
    if (params.search) searchParams.set("search", params.search);
    if (params.sortField) searchParams.set("sortField", params.sortField);
    if (params.sortOrder) searchParams.set("sortOrder", params.sortOrder);
    if (params.limit != null) searchParams.set("limit", String(params.limit));
    if (params.offset != null) searchParams.set("offset", String(params.offset));
    return fetcher.get<RefundQueueListResult>(
      `${config.apiUrl}/wallet/refund-queue?${searchParams.toString()}`,
    );
  },

  async approveRefund(
    id: string,
    totpCode?: string,
  ): Promise<{ ok: true } | { error: string }> {
    const res = await fetcher.post<{ ok: true } | { error: string }>(
      `${config.apiUrl}/wallet/refund-queue/${encodeURIComponent(id)}/approve`,
      totpCode ? { totpCode } : {},
    );
    return res as { ok: true } | { error: string };
  },

  async rejectRefund(id: string): Promise<{ ok: true } | { error: string }> {
    const res = await fetcher.post<{ ok: true } | { error: string }>(
      `${config.apiUrl}/wallet/refund-queue/${encodeURIComponent(id)}/reject`,
      {},
    );
    return res as { ok: true } | { error: string };
  },

  async listAccounts(
    params: WalletAccountsListParams = {},
  ): Promise<WalletAccountsListResult> {
    const searchParams = new URLSearchParams();
    if (params.status) searchParams.set("status", params.status);
    if (params.search) searchParams.set("search", params.search);
    if (params.sortField) searchParams.set("sortField", params.sortField);
    if (params.sortOrder) searchParams.set("sortOrder", params.sortOrder);
    if (params.limit != null) searchParams.set("limit", String(params.limit));
    if (params.offset != null) searchParams.set("offset", String(params.offset));
    return fetcher.get<WalletAccountsListResult>(
      `${config.apiUrl}/wallet/accounts?${searchParams.toString()}`,
    );
  },

  async getLedgerSummary(
    params: Omit<
      LedgerListParams,
      "limit" | "offset" | "sortField" | "sortOrder"
    > = {},
  ): Promise<LedgerSummary> {
    const searchParams = new URLSearchParams();
    if (params.walletId) searchParams.set("walletId", params.walletId);
    if (params.search) searchParams.set("search", params.search);
    if (params.createdFrom) searchParams.set("createdFrom", params.createdFrom);
    if (params.createdTo) searchParams.set("createdTo", params.createdTo);
    if (params.type) searchParams.set("type", params.type);
    return fetcher.get<LedgerSummary>(
      `${config.apiUrl}/wallet/ledger/summary?${searchParams.toString()}`,
    );
  },

  async listLedger(params: LedgerListParams = {}): Promise<LedgerListResult> {
    const searchParams = new URLSearchParams();
    if (params.walletId) searchParams.set("walletId", params.walletId);
    if (params.search) searchParams.set("search", params.search);
    if (params.createdFrom) searchParams.set("createdFrom", params.createdFrom);
    if (params.createdTo) searchParams.set("createdTo", params.createdTo);
    if (params.type) searchParams.set("type", params.type);
    if (params.sortField) searchParams.set("sortField", params.sortField);
    if (params.sortOrder) searchParams.set("sortOrder", params.sortOrder);
    if (params.limit != null) searchParams.set("limit", String(params.limit));
    if (params.offset != null) searchParams.set("offset", String(params.offset));
    return fetcher.get<LedgerListResult>(
      `${config.apiUrl}/wallet/ledger?${searchParams.toString()}`,
    );
  },

  async getWalletLimits(walletId: string): Promise<WalletLimitsView> {
    return fetcher.get<WalletLimitsView>(
      `${config.apiUrl}/wallet/limits/${encodeURIComponent(walletId)}`,
    );
  },

  async updateWalletLimits(
    walletId: string,
    payload: WalletLimitsUpdatePayload,
  ): Promise<WalletLimitsView> {
    return fetcher.put<WalletLimitsView>(
      `${config.apiUrl}/wallet/limits/${encodeURIComponent(walletId)}`,
      payload,
    );
  },

  /** ดึงข้อมูล ledger ตาม filter สำหรับ export (ไม่ paginate, สูงสุด 10,000 รายการ) */
  async getLedgerExport(
    params: Omit<
      LedgerListParams,
      "limit" | "offset" | "sortField" | "sortOrder"
    > = {},
  ): Promise<LedgerListResult> {
    const searchParams = new URLSearchParams();
    if (params.walletId) searchParams.set("walletId", params.walletId);
    if (params.search) searchParams.set("search", params.search);
    if (params.createdFrom) searchParams.set("createdFrom", params.createdFrom);
    if (params.createdTo) searchParams.set("createdTo", params.createdTo);
    if (params.type) searchParams.set("type", params.type);
    return fetcher.get<LedgerListResult>(
      `${config.apiUrl}/wallet/ledger/export?${searchParams.toString()}`,
    );
  },
};
